import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { capitalize } from 'lodash';
import moment from 'moment';
import { default as HasRoleSubmenu } from 'components/Submenu';
import AddUserModal from 'components/Modals/AddUserModal';
import EditUserModal from 'components/Modals/EditUserModal';
import UserDeleteModal from 'components/Modals/UserDeleteModal';
import ConfirmationModal from 'components/Modals/ConfirmationModal';
import {
  SUBMENU_TYPES,
  AUTH_ROLES,
  USER_FRIENDLY_AUTH_ROLES,
  AUTH_VALUES_TO_ROLES
} from 'utils/appConstants';
import { getUsers, createUser, editUser, removeUser } from 'api/users';
import { openErrorModal } from 'reducers/modal/actions';
import { toggleAccountMenuDropdown } from 'reducers/customer/actions';
import { FlexPage } from 'components/CommonStyles';
import { logoutUser } from 'reducers/auth/actions';
import Table from 'components/Table';

const Submenu = HasRoleSubmenu([AUTH_ROLES.ADMIN_CREATE_USER]);

const UserManagementPage = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;

  ${({ hasWriteAccess }) =>
    hasWriteAccess
      ? `
    .ReactTable .rt-tr {
      cursor: pointer !important;
    }
  `
      : null}
`;

const MODAL_STATES = {
  ADD_USER: 'ADD_USER',
  EDIT_USER: 'EDIT_USER',
  USER_ADDED_CONFIRMATION: 'USER_ADDED_CONFIRMATION',
  USER_EDITED_CONFIRMATION: 'USER_EDITED_CONFIRMATION',
  DELETE_USER: 'DELETE_USER'
};

const columnDefs = [
  {
    field: 'name',
    headerName: 'Name',
    cellRenderer: row => {
      return `${row.data.firstName} ${row.data.lastName}`;
    }
  },
  {
    headerName: 'Email',
    field: 'email'
  },
  {
    headerName: 'Permissions',
    field: 'roleName'
  },
  {
    field: 'lastLoggedIn',
    headerName: 'Last Logged In',
    cellRenderer: ({ value }) =>
      `${value ? moment(value).format('MM/DD/YY hh:mmA') : ''}`
  },
  {
    field: 'isVerified',
    headerName: 'Email Verified',
    cellRenderer: ({ value }) => `${capitalize(!!value)}`
  }
];
const pollRate = parseInt(window.env.REACT_APP_GLOBAL_POLL_RATE_FREQUENCY);

const UserManagement = () => {
  const dispatch = useDispatch();
  const authenticatedUser = useSelector(state => state.auth.authenticatedUser);
  const showAcctMenuDropdown = useSelector(
    state => state.customer.showAcctMenuDropdown
  );
  const interval = useRef(null);
  const [showSubmenuDropdown, setShowmenuDropDown] = useState(false);
  const [modalState, setModalState] = useState(null);
  const [newUserEmail, setNewUserEmail] = useState(null);
  const [userToEdit, setUserToEdit] = useState(null);
  const [users, setUsers] = useState([]);
  const userManagementTableRef = useRef(null);

  useEffect(() => {
    try {
      updateUsers();
      //Local polling, keep out of redux store
      interval.current = setInterval(() => {
        updateUsers();
      }, pollRate);
    } catch (err) {
      console.error(err);
    }
    return () => {
      clearInterval(interval.current);
    };
  }, []);

  useEffect(() => {
    if (!modalState) {
      setNewUserEmail(null);
      setUserToEdit(null);
    }
  }, [modalState]);

  const updateUsers = async () => {
    const users = await getUsers();
    setUsers(users);
  };

  const updateModalState = (event, state) => {
    if (event) {
      event.preventDefault();
    }
    setModalState(state);
  };

  const onAddUser = async user => {
    try {
      await createUser(user);
      setModalState(MODAL_STATES.USER_ADDED_CONFIRMATION);
      setNewUserEmail(user.email);
      updateUsers();
    } catch (err) {
      const errorMessage = 'Could not create new user';
      dispatch(openErrorModal(errorMessage));
    }
  };

  const onEditUser = async userInfo => {
    try {
      //Prevent removal of admin access of last administrator
      const usersWithAdminAccess = users.filter(
        user => user.roleName === 'AdminCreateUser' || user.roleName === 'Admin'
      );
      if (
        usersWithAdminAccess.length === 1 &&
        userInfo.id === usersWithAdminAccess[0].id &&
        !['AdminCreateUser', 'Admin'].includes(userInfo.role)
      ) {
        const errorMessage =
          'Changing access of the last administrator is forbidden';
        dispatch(openErrorModal(errorMessage));
        return;
      }
      await editUser(userInfo);
      setModalState(MODAL_STATES.USER_EDITED_CONFIRMATION);
      updateUsers();
    } catch (err) {
      const errorMessage = 'Could not edit user';
      dispatch(openErrorModal(errorMessage));
    }
  };

  const onDeleteUser = async event => {
    event.preventDefault();
    try {
      //Prevent deletion of last administrator
      const usersWithAdminAccess = users.filter(
        user => user.roleName === 'AdminCreateUser' || user.roleName === 'Admin'
      );
      if (
        usersWithAdminAccess.length === 1 &&
        userToEdit.id === authenticatedUser.id
      ) {
        const errorMessage = 'Removing the last administrator is forbidden';
        dispatch(openErrorModal(errorMessage));
        return;
      }
      await removeUser(userToEdit);
      const updatedUsers = users.filter(user => user.id !== userToEdit.id);
      if (
        userToEdit.id === authenticatedUser.id &&
        usersWithAdminAccess.length > 1
      ) {
        dispatch(logoutUser());
      }
      setModalState(null);
      setUsers(updatedUsers);
    } catch (err) {
      const errorMessage = 'Could not delete user';
      dispatch(openErrorModal(errorMessage));
    }
  };

  // If the sidepanel closes, deselect any selected items on the table
  useEffect(() => {
    if (userManagementTableRef.current?.api && !modalState) {
      userManagementTableRef.current.api.clearFocusedCell();
    }
  }, [modalState]);

  const toggleMenu = () => {
    if (showAcctMenuDropdown) {
      dispatch(toggleAccountMenuDropdown(false));
    }
    if (showSubmenuDropdown) {
      setShowmenuDropDown(false);
    }
  };

  //Translate DB role permissions to friendly
  const userMap = users.map(user => {
    let { roleName } = user;

    roleName =
      USER_FRIENDLY_AUTH_ROLES[AUTH_VALUES_TO_ROLES[roleName.toUpperCase()]];
    return { ...user, roleName };
  });

  const authenticatedUserHasWriteAccess =
    authenticatedUser.roleName === AUTH_ROLES.ADMIN_CREATE_USER;

  return (
    <FlexPage>
      <UserManagementPage
        hasWriteAccess={authenticatedUserHasWriteAccess}
        onClick={toggleMenu}
      >
        <Submenu
          dropdownIsShowing={showSubmenuDropdown}
          selectOption={() => setModalState(MODAL_STATES.ADD_USER)}
          toggleDropdownVisibility={isShowing => setShowmenuDropDown(isShowing)}
          submenuType={SUBMENU_TYPES.USERS}
        />
        <FlexPage>
          <Table
            bigTitle="Authorized Users"
            ref={userManagementTableRef}
            rowData={userMap}
            columnDefs={columnDefs}
            disableSelect={true}
            height="600px"
            width="1000px"
            onRowClicked={row => {
              if (authenticatedUserHasWriteAccess) {
                setUserToEdit(row.data);
                setModalState(MODAL_STATES.EDIT_USER);
              }
            }}
          />
          {/* Modal Conditional Rendering */}
          {modalState === MODAL_STATES.ADD_USER ? (
            <AddUserModal
              onClose={event => updateModalState(event, null)}
              onSubmit={onAddUser}
              users={users}
            />
          ) : modalState === MODAL_STATES.USER_ADDED_CONFIRMATION ? (
            <ConfirmationModal
              updateModalState={updateModalState}
              header="Send Account Invitation"
              message={`Account activation link sent to ${newUserEmail}`}
            />
          ) : modalState === MODAL_STATES.EDIT_USER ? (
            <EditUserModal
              user={userToEdit}
              users={users}
              onClose={event => updateModalState(event, null)}
              onSubmit={onEditUser}
              handleDelete={event =>
                updateModalState(event, MODAL_STATES.DELETE_USER)
              }
            />
          ) : modalState === MODAL_STATES.USER_EDITED_CONFIRMATION ? (
            <ConfirmationModal
              updateModalState={updateModalState}
              header="Edit User Info"
              message={`${userToEdit.firstName} ${userToEdit.lastName} has been updated`}
            />
          ) : modalState === MODAL_STATES.DELETE_USER ? (
            <UserDeleteModal
              name={`${userToEdit.firstName} ${userToEdit.lastName}`}
              onClose={event => updateModalState(event, null)}
              handleDelete={onDeleteUser}
              userToEdit={userToEdit}
              users={users}
            />
          ) : null}
        </FlexPage>
      </UserManagementPage>
    </FlexPage>
  );
};

export default UserManagement;
